import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Icon, KpiCard, Progress } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert } from '@ds/feedback';
import { SearchField } from '@ds/forms';
import { Menu, Pagination, Tabs } from '@ds/navigation';
import { ExportMenu } from '@/comuns/ExportMenu';
import { PageHeader } from '@/comuns/PageHeader';
import { dataSimples, moeda } from '@/comuns/formato';
import { concordar, custoDeConsumoCentavos, quantidade, unidadeAbrev, unidadeLabel } from '@/comuns/unidades';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { TODOS_OS_REGISTROS } from '@/comuns/exportar';
import type {
    AbaDoEstoque, CargaDoEstoque, CategoriaDeProduto, Fornecedor, Marca, MovimentacaoDeEstoque,
    Produto, ReservaDeEstoque, StatusDaReserva,
} from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { useRotaAtual } from '@/router/useRotaAtual';
import { CategoriaDeProdutoModal, FornecedorModal, MarcaModal } from './estoque/Cadastros';
import { MovimentacaoModal } from './estoque/MovimentacaoModal';
import { NovaReservaModal } from './estoque/NovaReservaModal';
import { ProdutoDetalhe, SITUACAO } from './estoque/ProdutoDetalhe';
import { ProdutoModal } from './estoque/ProdutoModal';

const POR_PAGINA = 10;

const ABAS: AbaDoEstoque[] = ['produtos', 'categorias', 'baixo', 'reservas', 'fornecedores', 'marcas', 'movimentacoes'];

const RESERVA: Record<StatusDaReserva, { rotulo: string; tom: 'info' | 'success' | 'danger' }> = {
    reservada: { rotulo: 'Reservado', tom: 'info' },
    consumida: { rotulo: 'Consumido', tom: 'success' },
    insuficiente: { rotulo: 'Insumo insuficiente', tom: 'danger' },
};

function abaDaRota(pathname: string): AbaDoEstoque {
    const ultimo = pathname.split('/').pop() ?? '';
    return (ABAS as string[]).includes(ultimo) ? (ultimo as AbaDoEstoque) : 'produtos';
}

export async function carregarEstoque({ request }: LoaderFunctionArgs): Promise<CargaDoEstoque> {
    const url = new URL(request.url);
    return dados().estoque({
        aba: abaDaRota(url.pathname),
        busca: url.searchParams.get('q') ?? undefined,
        pagina: Number(url.searchParams.get('pagina') ?? '1') || 1,
        porPagina: POR_PAGINA,
    });
}

export function Estoque() {
    const carga = useLoaderData() as CargaDoEstoque;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const handle = useRotaAtual();
    const aviso = useAviso();

    const [produtoAberto, setProdutoAberto] = useState<Produto | null>(null);
    const [produtoEmEdicao, setProdutoEmEdicao] = useState<Produto | 'novo' | null>(null);
    const [movimentacao, setMovimentacao] = useState<{ produto: Produto; tipo: 'entrada' | 'saida' } | null>(null);
    const [fornecedorEmEdicao, setFornecedorEmEdicao] = useState<Fornecedor | 'novo' | null>(null);
    const [marcaEmEdicao, setMarcaEmEdicao] = useState<Marca | 'novo' | null>(null);
    const [categoriaEmEdicao, setCategoriaEmEdicao] = useState<CategoriaDeProduto | 'novo' | null>(null);
    const [novaReserva, setNovaReserva] = useState(false);

    const rota = rotaPorId('estoque');
    const aba = carga.aba;
    const subrota = handle?.subrota?.path ?? 'produtos';
    const listaDeProdutos = aba === 'produtos' || aba === 'baixo';

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (!valor) proximos.delete(chave);
            else proximos.set(chave, valor);
            if (chave !== 'pagina') proximos.delete('pagina');
            return proximos;
        }, { replace: true });
    };

    const recarregar = () => revalidator.revalidate();

    const decidirReserva = async (reserva: ReservaDeEstoque, acao: 'baixar' | 'liberar') => {
        try {
            if (acao === 'baixar') {
                await dados().baixarReserva(reserva.id);
                aviso({ title: 'Baixa registrada', description: `${reserva.procedimento} · insumos consumidos` });
            } else {
                await dados().liberarReserva(reserva.id);
                aviso({ title: 'Reserva liberada', description: reserva.procedimento });
            }
            recarregar();
        } catch (e) {
            aviso({ title: 'Não foi possível concluir', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const colunasDeProduto: DataTableColumn<Produto>[] = [
        {
            key: 'nome', header: 'Produto', sortable: true,
            render: (p) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{p.nome}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {[p.marca, p.fornecedor, p.lote && `lote ${p.lote}`].filter(Boolean).join(' · ')}
                    </span>
                </span>
            ),
        },
        { key: 'validade', header: 'Validade', sortable: true, render: (p) => (p.validade ? dataSimples(p.validade) : '—') },
        {
            key: 'disponivel', header: 'Disponível', align: 'right',
            render: (p) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, minWidth: 96 }}>
                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {quantidade(p.disponivel)} {unidadeLabel(p.disponivel, p.unidade)}
                    </span>
                    <Progress
                        size="sm" style={{ width: 88 }} tone={SITUACAO[p.situacao].tom}
                        /* Uma barra por linha, todas iguais para quem ouve: sem nome,
                           o leitor anuncia uma lista de porcentagens sem assunto. */
                        ariaLabel={`Estoque de ${p.nome}`}
                        value={p.minimo > 0 ? Math.max(0, Math.min(100, (p.disponivel / (p.minimo * 2)) * 100)) : 100}
                    />
                </span>
            ),
        },
        {
            key: 'reservado', header: 'Reservado', align: 'right',
            render: (p) => (p.reservado > 0
                ? <span style={{ color: 'var(--info-strong)', fontWeight: 600 }}>{quantidade(p.reservado)} {unidadeLabel(p.reservado, p.unidade)}</span>
                : <span style={{ color: 'var(--text-tertiary)' }}>—</span>),
        },
        { key: 'minimo', header: 'Mínimo', align: 'right', render: (p) => `${quantidade(p.minimo)} ${unidadeLabel(p.minimo, p.unidade)}` },
        { key: 'categoria', header: 'Categoria', render: (p) => (p.categoria ? <Badge tone="neutral">{p.categoria}</Badge> : <span style={{ color: 'var(--text-tertiary)' }}>—</span>) },
        {
            key: 'custoCentavos', header: 'Custo unit.', align: 'right',
            render: (p) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ fontWeight: 600 }}>{moeda(p.custoCentavos)}</span>
                    {/* O custo por consumo é o número que entra no procedimento:
                        50 UI de um frasco de R$ 890 custam R$ 445, não R$ 44.500. */}
                    <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                        {moeda(custoDeConsumoCentavos(p))}/{unidadeAbrev(p.unidadeConsumo)}
                    </span>
                </span>
            ),
        },
        { key: 'situacao', header: 'Situação', render: (p) => <Badge tone={SITUACAO[p.situacao].tom} dot>{SITUACAO[p.situacao].rotulo}</Badge> },
        {
            key: 'acoes', header: '', align: 'right', width: 60,
            render: (p) => (
                <span data-parar-linha>
                    <Menu
                        align="right" width={210}
                        onSelect={(id) => {
                            if (id === 'entrada' || id === 'saida') setMovimentacao({ produto: p, tipo: id });
                            else setProdutoEmEdicao(p);
                        }}
                        trigger={<Button size="sm" variant="ghost" iconLeft={<Icon name="ellipsis" size={15} />} aria-label={`Mais ações — ${p.nome}`} />}
                        items={[
                            { id: 'entrada', label: 'Registrar entrada', icon: 'package-plus' },
                            { id: 'saida', label: 'Registrar saída', icon: 'package-minus' },
                            { divider: true },
                            { id: 'editar', label: 'Editar produto', icon: 'pencil' },
                        ]}
                    />
                </span>
            ),
        },
    ];

    const colunasDeCategoria: DataTableColumn<CategoriaDeProduto>[] = [
        {
            key: 'nome', header: 'Categoria', sortable: true,
            render: (c) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{c.nome}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{c.descricao ?? '—'}</span>
                </span>
            ),
        },
        { key: 'produtos', header: 'Produtos', align: 'right', render: (c) => (c.produtos ? String(c.produtos) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>) },
        { key: 'valorEmEstoqueCentavos', header: 'Valor em estoque', align: 'right', render: (c) => moeda(c.valorEmEstoqueCentavos) },
        { key: 'ativa', header: 'Situação', render: (c) => <Badge tone={c.ativa ? 'success' : 'neutral'} dot>{c.ativa ? 'Ativa' : 'Inativa'}</Badge> },
    ];

    const colunasDeFornecedor: DataTableColumn<Fornecedor>[] = [
        {
            key: 'nome', header: 'Fornecedor', sortable: true,
            render: (f) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{f.nome}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{f.cnpj || '—'}</span>
                </span>
            ),
        },
        {
            key: 'contato', header: 'Contato',
            render: (f) => (
                <span>
                    <span style={{ display: 'block' }}>{f.contato || '—'}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{f.telefone || '—'}</span>
                </span>
            ),
        },
        {
            key: 'marcas', header: 'Marcas',
            render: (f) => (f.marcas.length
                ? <span style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>{f.marcas.map((m) => <Badge key={m} tone="neutral">{m}</Badge>)}</span>
                : <span style={{ color: 'var(--text-tertiary)' }}>—</span>),
        },
        { key: 'prazoEntrega', header: 'Prazo de entrega', render: (f) => f.prazoEntrega ?? '—' },
        { key: 'ativo', header: 'Status', render: (f) => <Badge tone={f.ativo ? 'success' : 'neutral'} dot>{f.ativo ? 'Ativo' : 'Inativo'}</Badge> },
    ];

    const colunasDeMarca: DataTableColumn<Marca>[] = [
        {
            key: 'nome', header: 'Marca', sortable: true,
            render: (m) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{m.nome}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{m.fabricante ?? '—'}</span>
                </span>
            ),
        },
        { key: 'categoria', header: 'Categoria', render: (m) => m.categoria ?? '—' },
        { key: 'fornecedor', header: 'Fornecedor', render: (m) => m.fornecedor ?? '—' },
        { key: 'registro', header: 'Registro', render: (m) => m.registro ?? '—' },
        { key: 'produtos', header: 'Produtos', align: 'right' },
        { key: 'ativa', header: 'Status', render: (m) => <Badge tone={m.ativa ? 'success' : 'neutral'} dot>{m.ativa ? 'Ativa' : 'Inativa'}</Badge> },
    ];

    const colunasDeReserva: DataTableColumn<ReservaDeEstoque>[] = [
        {
            key: 'procedimento', header: 'Procedimento', sortable: true,
            render: (r) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{r.procedimento}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.origem}</span>
                </span>
            ),
        },
        { key: 'paciente', header: 'Paciente', render: (r) => r.paciente ?? '—' },
        { key: 'criadoEm', header: 'Data', sortable: true, render: (r) => dataSimples(r.criadoEm.slice(0, 10)) },
        {
            key: 'itens', header: 'Insumos',
            render: (r) => (
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12.5 }}>
                    {r.itens.map((i) => (
                        <span key={i.produtoId}>{quantidade(i.quantidadeConsumo)} {unidadeAbrev(i.unidadeConsumo)} · {i.produto}</span>
                    ))}
                </span>
            ),
        },
        { key: 'status', header: 'Status', render: (r) => <Badge tone={RESERVA[r.status].tom} dot>{RESERVA[r.status].rotulo}</Badge> },
        {
            key: 'acoes', header: '', align: 'right', width: 190,
            render: (r) => (r.status === 'consumida' ? null : (
                <span data-parar-linha style={{ display: 'inline-flex', gap: 6 }}>
                    <Button size="sm" variant="secondary" onClick={() => void decidirReserva(r, 'baixar')}>Dar baixa</Button>
                    <Button size="sm" variant="ghost" onClick={() => void decidirReserva(r, 'liberar')}>Liberar</Button>
                </span>
            )),
        },
    ];

    const colunasDeMovimentacao: DataTableColumn<MovimentacaoDeEstoque>[] = [
        { key: 'ocorridoEm', header: 'Data', sortable: true, render: (m) => dataSimples(m.ocorridoEm.slice(0, 10)) },
        { key: 'produto', header: 'Produto' },
        { key: 'tipo', header: 'Tipo', render: (m) => <Badge tone={m.tipo === 'entrada' ? 'success' : 'neutral'} dot>{m.tipo === 'entrada' ? 'Entrada' : 'Saída'}</Badge> },
        {
            key: 'quantidade', header: 'Quantidade', align: 'right',
            render: (m) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {m.tipo === 'entrada' ? '+' : '−'}{quantidade(m.quantidade)} {unidadeLabel(m.quantidade, m.unidade)}
                    </span>
                    {/* Substantivo, e não particípio: "usadas" ou "usados"
                        dependeria do gênero da unidade — UI é feminina, frasco
                        não —, e a tabela não tem como saber. */}
                    {m.quantidadeConsumo != null && (
                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                            consumo: {quantidade(m.quantidadeConsumo)} {unidadeAbrev(m.unidadeConsumo)}
                        </span>
                    )}
                </span>
            ),
        },
        { key: 'origem', header: 'Origem / destino' },
    ];

    const novoDaAba = aba === 'fornecedores' ? { rotulo: 'Novo fornecedor', abrir: () => setFornecedorEmEdicao('novo') }
        : aba === 'marcas' ? { rotulo: 'Nova marca', abrir: () => setMarcaEmEdicao('novo') }
            : aba === 'categorias' ? { rotulo: 'Nova categoria', abrir: () => setCategoriaEmEdicao('novo') }
                : aba === 'reservas' ? { rotulo: 'Nova reserva', abrir: () => setNovaReserva(true) }
                : aba === 'movimentacoes' ? null
                    : { rotulo: 'Novo produto', abrir: () => setProdutoEmEdicao('novo') };

    const rotuloDaLista = aba === 'categorias' ? 'categorias'
        : aba === 'reservas' ? 'reservas'
            : aba === 'fornecedores' ? 'fornecedores'
                : aba === 'marcas' ? 'marcas'
                    : aba === 'movimentacoes' ? 'movimentações'
                        : 'itens';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Estoque"
                description="Produtos, fornecedores, marcas e o consumo que vem dos procedimentos"
                actions={
                    <>
                        {listaDeProdutos && (
                            <ExportMenu<Produto>
                                label="Exportar" size="md"
                                carregarTudo={async () => (await dados().estoque({
                                    aba, busca: params.get('q') ?? undefined, pagina: 1, porPagina: TODOS_OS_REGISTROS,
                                })).produtos}
                                payload={{
                                    title: 'Estoque', subtitle: `${carga.total} ${rotuloDaLista}`, rows: carga.produtos,
                                    columns: [
                                        { header: 'Produto', value: (p) => p.nome },
                                        { header: 'Categoria', value: (p) => p.categoria ?? '' },
                                        { header: 'Marca', value: (p) => p.marca ?? '' },
                                        { header: 'Fornecedor', value: (p) => p.fornecedor ?? '' },
                                        { header: 'Lote', value: (p) => p.lote },
                                        { header: 'Validade', value: (p) => (p.validade ? dataSimples(p.validade) : '') },
                                        { header: 'Disponível', value: (p) => `${quantidade(p.disponivel)} ${unidadeLabel(p.disponivel, p.unidade)}` },
                                        { header: 'Mínimo', value: (p) => quantidade(p.minimo) },
                                        { header: 'Custo unitário', value: (p) => moeda(p.custoCentavos) },
                                        { header: 'Custo por consumo', value: (p) => `${moeda(custoDeConsumoCentavos(p))}/${unidadeAbrev(p.unidadeConsumo)}` },
                                        { header: 'Situação', value: (p) => SITUACAO[p.situacao].rotulo },
                                    ],
                                }}
                            />
                        )}
                        {aba === 'movimentacoes' && (
                            <ExportMenu<MovimentacaoDeEstoque>
                                label="Exportar" size="md"
                                carregarTudo={async () => (await dados().estoque({
                                    aba, busca: params.get('q') ?? undefined, pagina: 1, porPagina: TODOS_OS_REGISTROS,
                                })).movimentacoes}
                                payload={{
                                    title: 'Movimentações de estoque', subtitle: `${carga.total} movimentações`, rows: carga.movimentacoes,
                                    columns: [
                                        { header: 'Data', value: (m) => dataSimples(m.ocorridoEm.slice(0, 10)) },
                                        { header: 'Produto', value: (m) => m.produto },
                                        { header: 'Tipo', value: (m) => (m.tipo === 'entrada' ? 'Entrada' : 'Saída') },
                                        { header: 'Quantidade', value: (m) => `${quantidade(m.quantidade)} ${unidadeLabel(m.quantidade, m.unidade)}` },
                                        { header: 'Consumo', value: (m) => (m.quantidadeConsumo != null ? `${quantidade(m.quantidadeConsumo)} ${unidadeAbrev(m.unidadeConsumo)}` : '') },
                                        { header: 'Origem / destino', value: (m) => m.origem },
                                    ],
                                }}
                            />
                        )}
                        {novoDaAba && (
                            <Button iconLeft={<Icon name="plus" size={15} />} onClick={novoDaAba.abrir}>{novoDaAba.rotulo}</Button>
                        )}
                    </>
                }
            >
                <Tabs
                    ariaLabel="Seções do estoque" activeId={subrota}
                    onChange={(id) => navigate({ pathname: `/app/estoque/${id}`, search: params.get('q') ? `?q=${params.get('q')}` : '' })}
                    tabs={(rota?.children ?? []).map((c) => ({
                        id: c.path,
                        label: c.label,
                        count: c.path === 'produtos' ? carga.totais.produtos
                            : c.path === 'categorias' ? carga.totais.categorias
                                : c.path === 'baixo' ? carga.totais.abaixoDoMinimo
                                    : c.path === 'reservas' ? carga.totais.reservasAbertas
                                        : c.path === 'fornecedores' ? carga.totais.fornecedores
                                            : c.path === 'marcas' ? carga.totais.marcas
                                                : carga.totais.movimentacoes,
                    }))}
                />
            </PageHeader>

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                {carga.indicadores.map((i) => (
                    <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />} />
                ))}
            </div>

            {/* O alerta fala do estoque inteiro, não da página aberta: item
                crítico na página 2 é o mesmo problema. E leva à sugestão de
                compra — avisar sem dar o caminho é o que ele fazia antes. */}
            {carga.criticos.length > 0 && (
                <Alert
                    tone="danger" dismissible={false}
                    title={`${carga.criticos.length} ${carga.criticos.length === 1 ? 'item em nível crítico' : 'itens em nível crítico'}`}
                    action={(
                        <Button size="sm" variant="secondary" onClick={() => navigate('/app/compras/sugestao')}>
                            Criar pedido de compra
                        </Button>
                    )}
                >
                    {carga.criticos.map((p) => (
                        `${p.nome}: ${quantidade(p.disponivel)} ${unidadeLabel(p.disponivel, p.unidade)} ${concordar(p.disponivel, 'disponível', 'disponíveis')} contra mínimo de ${quantidade(p.minimo)}`
                    )).join(' · ')}
                </Alert>
            )}

            {aba === 'reservas' && (
                <Alert tone="info" dismissible={false} title="De onde vêm as reservas">
                    A reserva nasce quando uma proposta é aceita em Vendas ou no CRM — ou à mão, em Nova reserva. Dar baixa
                    consome o insumo e gera a movimentação de saída; liberar devolve o disponível sem movimentar, porque nada
                    saiu do armário.
                </Alert>
            )}
            {novaReserva && <NovaReservaModal onFechar={() => setNovaReserva(false)} onReservada={recarregar} />}

            <FilterBar
                search={(
                    <SearchField
                        value={params.get('q') ?? ''} onChange={(v) => trocarParam('q', v || null)} width={260}
                        placeholder={aba === 'fornecedores' ? 'Buscar fornecedor' : aba === 'marcas' ? 'Buscar marca' : aba === 'reservas' ? 'Buscar procedimento ou paciente' : 'Buscar produto ou lote'}
                    />
                )}
                resultLabel={`${carga.total} ${rotuloDaLista}`}
            />

            {listaDeProdutos && (
                <DataTable
                    columns={colunasDeProduto} rows={carga.produtos} rowKey="id"
                    onRowClick={setProdutoAberto} rowLabel={(p) => p.nome}
                    emptyMessage={aba === 'baixo' ? 'Nenhum item abaixo do mínimo.' : 'Nenhum produto encontrado.'}
                />
            )}

            {aba === 'categorias' && (
                <DataTable
                    columns={colunasDeCategoria} rows={carga.categorias} rowKey="id"
                    onRowClick={setCategoriaEmEdicao} rowLabel={(c) => c.nome}
                    emptyMessage="Nenhuma categoria cadastrada."
                />
            )}

            {aba === 'fornecedores' && (
                <DataTable
                    columns={colunasDeFornecedor} rows={carga.fornecedores} rowKey="id"
                    onRowClick={setFornecedorEmEdicao} rowLabel={(f) => f.nome}
                    emptyMessage="Nenhum fornecedor cadastrado."
                />
            )}

            {aba === 'marcas' && (
                <DataTable
                    columns={colunasDeMarca} rows={carga.marcas} rowKey="id"
                    onRowClick={setMarcaEmEdicao} rowLabel={(m) => m.nome}
                    emptyMessage="Nenhuma marca cadastrada."
                />
            )}

            {aba === 'reservas' && (
                <DataTable
                    columns={colunasDeReserva} rows={carga.reservas} rowKey="id"
                    rowLabel={(r) => `${r.procedimento} · ${r.paciente ?? ''}`}
                    emptyMessage="Nenhuma reserva aberta."
                />
            )}

            {aba === 'movimentacoes' && (
                <DataTable
                    columns={colunasDeMovimentacao} rows={carga.movimentacoes} rowKey="id"
                    rowLabel={(m) => `${m.produto} · ${m.origem}`}
                    emptyMessage="Nenhuma movimentação registrada."
                />
            )}

            <Pagination
                page={carga.pagina} pageCount={carga.paginas} total={carga.total} pageSize={POR_PAGINA}
                onChange={(p) => trocarParam('pagina', String(p))}
            />

            <ProdutoDetalhe
                produto={produtoAberto} movimentacoes={carga.movimentacoes}
                onClose={() => setProdutoAberto(null)}
                onEditar={(p) => { setProdutoAberto(null); setProdutoEmEdicao(p); }}
                onMovimentar={(p, tipo) => { setProdutoAberto(null); setMovimentacao({ produto: p, tipo }); }}
                onRepor={() => { setProdutoAberto(null); navigate('/app/compras/sugestao'); }}
            />

            <ProdutoModal
                alvo={produtoEmEdicao} categorias={carga.categorias} marcas={carga.marcas} fornecedores={carga.fornecedores}
                onClose={() => setProdutoEmEdicao(null)} onSalvo={recarregar}
            />

            <MovimentacaoModal
                alvo={movimentacao} fornecedores={carga.fornecedores}
                onClose={() => setMovimentacao(null)} onRegistrada={recarregar}
            />

            <FornecedorModal alvo={fornecedorEmEdicao} onClose={() => setFornecedorEmEdicao(null)} onSalvo={recarregar} />
            <MarcaModal alvo={marcaEmEdicao} fornecedores={carga.fornecedores} onClose={() => setMarcaEmEdicao(null)} onSalvo={recarregar} />
            <CategoriaDeProdutoModal alvo={categoriaEmEdicao} onClose={() => setCategoriaEmEdicao(null)} onSalva={recarregar} />
        </div>
    );
}
